import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type RootStateOrAny } from 'src/store';
import { TddSyncInputs } from './TddSyncInputs';
import { type SyncLink, SyncMetrics, SyncParams } from './SyncParamsTypes';
import './TddSyncTab.css';
import { LPTable } from 'src/components/tables/LPTable';
import { useLpTable } from 'src/components/tables/internal/useLpTable';
import { Loader } from 'semantic-ui-react';
import { getTddSyncColumns, type TddSyncLink } from './TddSyncColumns';

export interface TddSyncTabProps {
  licensed?: boolean;
  syncParams: SyncParams;
  fieldPrefix: string;
  onSettingChange?: (band: string, settings: any) => void;
  isSaving?: boolean;
  links?: SyncLink[];
  metrics?: SyncMetrics | null;
  linksIsLoading?: boolean;
  linksError?: unknown;
}

function TddSyncTable({ links }: { links: SyncLink[] }) {
  const { prefs } = useSelector((state: RootStateOrAny) => state.mainFrame);
  const { rangeUnits } = prefs;

  // Augment links with className for invalid rows and status for link_ok
  const augmentedLinks: TddSyncLink[] = useMemo(
    () =>
      links.map((link) => {
        const frameDuration = String(link.frame_duration || '').toLowerCase();
        const slaveGap = String(link.slave_rx_tx_gap || '').toLowerCase();
        const hasInvalid =
          frameDuration.includes('invalid') || slaveGap.includes('invalid');

        // Build className based on both invalid state and link_ok
        const classNames = [];
        if (hasInvalid) classNames.push('tddsync-invalid-row');
        if (link.link_ok === false) classNames.push('status-error');

        return {
          ...(link as unknown as Omit<TddSyncLink, 'showSelectionColumn'>),
          className: classNames.length > 0 ? classNames.join(' ') : undefined,
          showSelectionColumn: false,
        } as TddSyncLink;
      }),
    [links]
  );

  const columns = useMemo(
    () => getTddSyncColumns(rangeUnits, prefs),
    [rangeUnits, prefs]
  );

  const visibleColumns = useMemo(
    () => columns.map((col) => col.id as string),
    [columns]
  );

  const model = useLpTable<TddSyncLink>({
    data: augmentedLinks,
    visibleColumns,
    columns,
    getRowId: (row) => row.id,
    // No column pinning, sort by name by default
    options: {
      defaultPinnedColumn: '',
      showSelectionColumn: false,
      initialSort: [{ id: 'name', desc: false }],
    },
  });

  return (
    <div className="tddsync-table">
      <LPTable<TddSyncLink>
        queryKey={['tddSyncTable']}
        model={model}
        tableId="tddSyncTable"
        showChooseColumns={false}
      />
    </div>
  );
}

const TddSyncTab: React.FC<TddSyncTabProps> = ({
  syncParams,
  fieldPrefix,
  onSettingChange,
  isSaving,
  links,
  metrics,
  linksIsLoading,
  linksError,
}) => {
  if (!syncParams) {
    return (
      <div className="tddsync-empty-message">
        This project has no synchronized links in this band
      </div>
    );
  }

  return (
    <div className="tdd-sync-tab-content">
      <TddSyncInputs
        syncParams={syncParams}
        metrics={metrics}
        fieldPrefix={fieldPrefix}
        onSettingChange={onSettingChange}
        isSaving={isSaving}
      />
      {linksIsLoading ? (
        <div className="tddsync-table">
          <Loader active inline="centered" />
        </div>
      ) : linksError ? (
        <div className="tddsync-empty-message">
          Failed to load synchronized links
        </div>
      ) : links && links.length > 0 ? (
        <TddSyncTable links={links} />
      ) : (
        <div className="tddsync-empty-message">No synchronized links</div>
      )}
    </div>
  );
};

export default TddSyncTab;
